<template>
  <div class="modern-login-root">
    <div ref="particleLayer" class="particle-bg"></div>
    <div class="modern-login-split">
      <div class="modern-login-left">
        <div class="header-title">
          <span class="glow-text">云商城 - 微服务交易平台</span>
        </div>
        <div class="left-content">
          <Cart3D style="margin-bottom: 32px;" />
          <div class="typewriter">
            <span>{{ slogan }}</span>
            <span class="type-cursor">|</span>
          </div>
        </div>
      </div>
      <div class="modern-login-right">
        <div class="glassmorphism login-card">
          <h2 class="glow-text" style="margin-bottom: 18px;">创建新账号</h2>
          <el-form :model="registerForm" :rules="registerRules" ref="registerFormRef" class="login-form" @keyup.enter="handleRegister">
            <el-form-item prop="username">
              <el-input v-model="registerForm.username" placeholder="用户名" clearable autofocus />
            </el-form-item>
            <el-form-item prop="password">
              <el-input v-model="registerForm.password" type="password" placeholder="密码" show-password />
            </el-form-item>
            <el-form-item prop="confirmPassword">
              <el-input v-model="registerForm.confirmPassword" type="password" placeholder="确认密码" show-password />
            </el-form-item>
            <el-form-item prop="nickname">
              <el-input v-model="registerForm.nickname" placeholder="昵称" clearable />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" class="ripple login-btn" :loading="loading" @click="handleRegister" style="width:100%;font-size:18px;">注 册</el-button>
            </el-form-item>
            <el-form-item style="margin-bottom:0;">
              <el-link type="info" @click="goToLogin">已有账号？登录</el-link>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </div>
    <el-dialog 
      v-model="showLoading" 
      width="300px" 
      :show-close="false" 
      :close-on-click-modal="false" 
      :close-on-press-escape="false" 
      align-center
      class="loading-dialog"
    >
      <div class="loading-content">
        <div class="loading-spinner">
          <el-icon :size="64" color="#a3a3ff" class="spin-icon"><Loading /></el-icon>
        </div>
        <div class="loading-text">注册成功</div>
        <div class="loading-subtext">正在跳转到登录页</div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElNotification, ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import { authAPI } from '../api/auth'
import Cart3D from './Cart3D.vue'
import { useTypewriter } from './useTypewriter'

const router = useRouter()
const registerForm = reactive({ username: '', password: '', confirmPassword: '', nickname: '' })
const registerFormRef = ref(null)
const loading = ref(false)
const showLoading = ref(false)
const slogan = useTypewriter([
  '探索未来交易的无限可能',
  '安全 · 高效 · 智能 · 云端',
  '让每一次交易更简单',
])

const validateConfirmPassword = (rule, value, callback) => {
  if (value === '') {
    callback(new Error('请再次输入密码'))
  } else if (value !== registerForm.password) {
    callback(new Error('两次输入密码不一致!'))
  } else {
    callback()
  }
}
const registerRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, validator: validateConfirmPassword, trigger: 'blur' }
  ],
  nickname: [
    { required: true, message: '请输入昵称', trigger: 'blur' },
    { min: 2, max: 20, message: '昵称长度在 2 到 20 个字符', trigger: 'blur' }
  ]
}

const handleRegister = async () => {
  if (!registerFormRef.value) return
  try {
    const valid = await registerFormRef.value.validate()
    if (!valid) return
    loading.value = true
    const response = await authAPI.register(
      registerForm.username,
      registerForm.password,
      registerForm.nickname
    )
    const { success, message, data } = response.data || {}
    const isSuccess = success !== false || data?.success !== false
    if (isSuccess) {
      showLoading.value = true
      setTimeout(() => {
        showLoading.value = false
        ElNotification({
          title: '注册成功',
          message: `欢迎，${registerForm.nickname || registerForm.username}，请登录体验云商城！`,
          type: 'success',
          duration: 2200,
        })
        router.push('/login')
      }, 1200)
    } else {
      ElMessage.error(message || '注册失败')
    }
  } catch (error) {
    console.error('注册异常:', error)
    const errMsg = error.response?.data?.message || error.message || '网络或服务器连接失败，请检查后端服务是否运行'
    ElMessage.error(errMsg)
  } finally {
    loading.value = false
  }
}

const goToLogin = () => {
  router.push('/login')
}

// 动态生成粒子
const particleLayer = ref(null)
onMounted(() => {
  const layer = particleLayer.value
  if (!layer) return
  layer.innerHTML = ''
  for (let i = 0; i < 32; i++) {
    const p = document.createElement('div')
    p.className = 'particle'
    const size = Math.random() * 18 + 12
    p.style.width = `${size}px`
    p.style.height = `${size}px`
    p.style.left = `${Math.random() * 100}%`
    p.style.top = `${Math.random() * 100}%`
    p.style.animationDuration = `${10 + Math.random() * 8}s`
    p.style.opacity = `${0.18 + Math.random() * 0.25}`
    layer.appendChild(p)
  }
})
</script>

<style scoped>
.modern-login-root {
  width: 100vw;
  height: 100vh;
  min-height: 600px;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
}
.modern-login-split {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  position: relative;
  z-index: 2;
}
.modern-login-left {
  width: 60vw;
  min-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 7vh;
  background: transparent;
  user-select: none;
}
.header-title {
  font-size: 2.2rem;
  margin-bottom: 36px;
  letter-spacing: 2px;
  text-align: center;
}
.left-content {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.typewriter {
  font-size: 1.4rem;
  color: #fff;
  margin-top: 12px;
  min-height: 2.2em;
  letter-spacing: 1.5px;
  font-family: 'JetBrains Mono', 'Consolas', monospace;
  background: rgba(76, 32, 103, 0.08);
  border-radius: 8px;
  padding: 8px 18px;
  box-shadow: 0 2px 12px 0 #4b206720;
  display: inline-block;
}
.type-cursor {
  color: #a3a3ff;
  animation: blink 1.1s steps(1) infinite;
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
.modern-login-right {
  width: 40vw;
  min-width: 340px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 3;
}
.login-card {
  width: 370px;
  padding: 38px 36px 28px 36px;
  box-sizing: border-box;
  margin: 0 auto;
  box-shadow: 0 8px 32px 0 #4b206733, 0 1.5px 8px 0 #232a4d22;
}
.login-form {
  margin-top: 8px;
}
.login-btn {
  margin-top: 8px;
  font-weight: bold;
  letter-spacing: 1px;
  border-radius: 8px;
  height: 44px;
}

/* 加载动画样式 */
.loading-dialog :deep(.el-dialog) {
  background: rgba(35, 42, 77, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(163, 163, 255, 0.3);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.loading-dialog :deep(.el-dialog__body) {
  padding: 40px 30px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-spinner {
  position: relative;
}

.spin-icon {
  animation: spin 1s linear infinite;
  filter: drop-shadow(0 0 12px rgba(163, 163, 255, 0.6));
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: 1px;
}

.loading-subtext {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@media (max-width: 900px) {
  .modern-login-left { display: none; }
  .modern-login-right { width: 100vw; min-width: unset; }
}
</style>